/**
 * Custom hook for dashboard data fetching, caching, and filter management.
 *
 * @returns Dashboard state including data, loading, filters, and action handlers.
 */
import { useState, useEffect, useRef } from 'react';

import axios from 'axios';
import api, { qualityAPI, customersAPI, coreAPI, performanceSettingsAPI } from '../lib/api';
import type { Company, Site, CompanyGroup, Warehouse } from '../lib/api';
import { getApiErrorMessage } from '../lib/api/types/common';
import type {
  DashboardData,
  DashboardFilterState,
  SavedDashboardFilter,
  ToastState,
  CommodityGroup,
  CommodityType,
} from '../components/dashboard/types';
import { getLast12MonthsRange } from '../components/dashboard/DashboardFilters';
import { Cache } from '../lib/cache';
import { usePreferences } from '../contexts/PreferencesContext';
import { useView } from '../contexts/ViewContext';
import { usePortalMode } from '../hooks/usePortalMode';

export interface UseDashboardDataReturn {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  filters: DashboardFilterState;
  setFilters: React.Dispatch<React.SetStateAction<DashboardFilterState>>;
  customers: Company[];
  suppliers: Company[];
  sites: Site[];
  warehouses: Warehouse[];
  customerGroups: CompanyGroup[];
  commodityGroups: CommodityGroup[];
  commodityTypes: CommodityType[];
  savedFilters: SavedDashboardFilter[];
  activeFilterPresetId: number | null;
  showSaveFilterDialog: boolean;
  setShowSaveFilterDialog: (show: boolean) => void;
  filterName: string;
  setFilterName: (name: string) => void;
  filterDescription: string;
  setFilterDescription: (desc: string) => void;
  isShared: boolean;
  setIsShared: (shared: boolean) => void;
  isDefault: boolean;
  setIsDefault: (isDefault: boolean) => void;
  toast: ToastState;
  setToast: React.Dispatch<React.SetStateAction<ToastState>>;
  exporting: boolean;
  dataPeriod: 'weekly' | 'monthly';
  setDataPeriod: (period: 'weekly' | 'monthly') => void;
  orderType: 'all' | 'scheduled' | 'spot';
  setOrderType: (type: 'all' | 'scheduled' | 'spot') => void;
  showToast: (message: string, type?: 'success' | 'error') => void;
  handleApplyFilters: () => void;
  handleResetFilters: () => void;
  handleSaveFilter: () => Promise<void>;
  loadFilterPreset: (preset: SavedDashboardFilter, skipFetch?: boolean) => void;
  fetchDashboardData: (forceRefresh?: boolean, filterOverrides?: DashboardFilterState, bypassServerCache?: boolean) => Promise<void>;
  // CR-4760: Pass the active tab so the CSV reflects what's on screen.
  // Compliance tab gets a focused compliance-only export instead of the
  // hardcoded purchasing metrics.
  handleExportReport: (activeTab?: string) => Promise<void>;
  loadDateAdherence: () => Promise<void>;
  loadCompanyKpi: () => Promise<void>;
  loadGroupKpi: () => Promise<void>;
  loadCostData: () => Promise<void>;
}

export function useDashboardData(_isComplianceMode: boolean): UseDashboardDataReturn {
  const { preferences, isLoading: prefsLoading } = usePreferences();
  const { viewLevel, selectedCompanyId, selectedSiteId, selectedGroupId, getViewQueryParams,
          selectedCompanyName, selectedSiteName, selectedGroupName, isLoading: viewContextLoading } = useView();
  const { isCustomer: portalIsCustomer, isHelix: portalIsHelix } = usePortalMode();

  const [dataPeriod, setDataPeriod] = useState<'weekly' | 'monthly'>('monthly');
  const [orderType, setOrderType] = useState<'all' | 'scheduled' | 'spot'>('all');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultDateRange = getLast12MonthsRange();
  const [filters, setFilters] = useState<DashboardFilterState>({
    dateFrom: defaultDateRange.dateFrom,
    dateTo: defaultDateRange.dateTo,
    customer: '',
    supplier: '',
    site: '',
    currency: preferences.currency,
    customerGroup: '',
    strategicOnly: false,
    customerType: '',
    commodityGroup: '',
    commodityType: '',
    siteCountry: '',
    customerCountry: '',
    customerCode: '',
    orderType: '',
    warehouse: '',
    dataMode: 'historical',
    division: '',
    // CR-4800
    approvalStage: '',
    riskCategory: '',
  });

  const [customers, setCustomers] = useState<Company[]>([]);
  const [suppliers, setSuppliers] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CompanyGroup[]>([]);
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);

  const [savedFilters, setSavedFilters] = useState<SavedDashboardFilter[]>([]);
  const [activeFilterPresetId, setActiveFilterPresetId] = useState<number | null>(null);
  const [showSaveFilterDialog, setShowSaveFilterDialog] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  const [toast, setToast] = useState<ToastState>({ message: '', type: 'success', visible: false });
  const [exporting, setExporting] = useState(false);

  const isInitialized = useRef(false);
  const fetchInProgress = useRef(false);
  const hasInitiallyLoaded = useRef(false);
  const fetchIdRef = useRef(0);
  const prevCurrency = useRef<string | undefined>(undefined);
  const viewContextInitialized = useRef(false);
  const currencyInitialized = useRef(false);
  const prevPortalModeRef = useRef(portalIsCustomer);
  const prevDataPeriod = useRef(dataPeriod);
  const prevOrderType = useRef(orderType);
  const prevViewLevel = useRef(viewLevel);
  const prevSelectedCompanyId = useRef(selectedCompanyId);
  const prevSelectedSiteId = useRef(selectedSiteId);
  const prevSelectedGroupId = useRef(selectedGroupId);
  const includeDateAdherenceRef = useRef(false);
  // Per-tab lazy-load flags. Default tab only needs summary KPIs + charts +
  // customer rankings; these heavier sections are fetched on demand when
  // the user opens the matching tab. Once loaded, the flag stays sticky so
  // subsequent refreshes keep the data fresh.
  const includeCompanyKpiRef = useRef(false);
  const includeGroupKpiRef = useRef(false);
  const includeCostDataRef = useRef(false);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 3000);
  };

  // Single initialization effect — wait for view context AND preferences to load
  // before first fetch, so the correct currency is used from the start
  useEffect(() => {
    if (isInitialized.current) return;
    if (viewContextLoading || prefsLoading) return;
    isInitialized.current = true;
    fetchDashboardData(true);
    fetchFilterOptions();
    fetchSavedFilters();
  }, [viewContextLoading, prefsLoading]);

  // Refetch on portal mode change
  useEffect(() => {
    if (!hasInitiallyLoaded.current) return;
    if (prevPortalModeRef.current === portalIsCustomer) return;
    prevPortalModeRef.current = portalIsCustomer;
    fetchDashboardData(true);
  }, [portalIsCustomer]);

  // Track view context initialization
  useEffect(() => {
    if (viewContextLoading) return;
    if (!viewContextInitialized.current) viewContextInitialized.current = true;
  }, [viewContextLoading]);

  // Refetch when view context (site/company/group selection) changes.
  // Always update refs and refetch — do NOT gate on hasInitiallyLoaded,
  // because the initial fetch may finish before ViewContext loads and
  // we'd miss the first site/company/group change.
  useEffect(() => {
    const viewLevelChanged = prevViewLevel.current !== viewLevel;
    const companyChanged = prevSelectedCompanyId.current !== selectedCompanyId;
    const siteChanged = prevSelectedSiteId.current !== selectedSiteId;
    const groupChanged = prevSelectedGroupId.current !== selectedGroupId;
    prevViewLevel.current = viewLevel;
    prevSelectedCompanyId.current = selectedCompanyId;
    prevSelectedSiteId.current = selectedSiteId;
    prevSelectedGroupId.current = selectedGroupId;
    if (viewLevelChanged || companyChanged || siteChanged || groupChanged) {
      fetchDashboardData(true);
      // CR-4887: For customer users, the supplier hierarchy backing the
      // Company Site / Division filters changes when the user switches
      // their authorised customer site (the backend keys suppliers off
      // profile.current_site_id). Refetch the filter options so the
      // dropdowns reflect the new customer site's supplier set.
      if (portalIsCustomer) fetchFilterOptions();
    }
  }, [viewLevel, selectedCompanyId, selectedSiteId, selectedGroupId]);

  // Refetch on currency change
  useEffect(() => {
    if (!hasInitiallyLoaded.current || !viewContextInitialized.current) return;
    if (!currencyInitialized.current) {
      currencyInitialized.current = true;
      prevCurrency.current = preferences.currency;
      return;
    }
    const currencyChanged = prevCurrency.current !== preferences.currency;
    prevCurrency.current = preferences.currency;
    if (currencyChanged && preferences.currency) fetchDashboardData(true);
  }, [preferences.currency]);

  // Refetch on period/order type change
  useEffect(() => {
    const periodChanged = prevDataPeriod.current !== dataPeriod;
    const orderChanged = prevOrderType.current !== orderType;
    prevDataPeriod.current = dataPeriod;
    prevOrderType.current = orderType;
    if (hasInitiallyLoaded.current && (periodChanged || orderChanged)) fetchDashboardData(true);
  }, [dataPeriod, orderType]);

  const fetchFilterOptions = async () => {
    const isHelixUser = portalIsHelix;
    try {
      if (isHelixUser) {
        // CR-5322: route the Customer-Site dropdown through for-filter so
        // partially-onboarded customers (draft / pending_initial / invited /
        // due_diligence / not_approved) don't leak into the picker before
        // they're approved. Suppliers and groups still come from the
        // generic list — those are SUPPLIER companies / company-groups,
        // unaffected by the onboarding state machine.
        const [customersRes, suppliersRes, groupsRes, warehousesRes] = await Promise.all([
          coreAPI.getCustomerSitesForFilter().catch(() => ({ data: { results: [] } })),
          coreAPI.getCompanies({ company_type: 'supplier', page_size: 1000 }),
          coreAPI.getCompanyGroups({ company_type: 'customer' }),
          coreAPI.getWarehouses().catch(() => ({ data: { results: [] } })),
        ]);
        setCustomers(customersRes.data.results || []);
        const supplierCompanies = suppliersRes.data.results || suppliersRes.data || [];
        setSuppliers(supplierCompanies);
        setSites(supplierCompanies as unknown as Site[]);
        setCustomerGroups(groupsRes.data.results || groupsRes.data || []);
        setWarehouses(warehousesRes.data.results || warehousesRes.data || []);
      } else {
        // CR-4887: customer-portal users want SUPPLIER hierarchy in the
        // Company Site / Division dropdowns, not their own customer group.
        // The opt-in ?context=supplier_filter parameter flips the accessible
        // endpoints to return suppliers-served. Supplier-portal users get
        // their own group siblings as before.
        const filterContext = portalIsCustomer ? { context: 'supplier_filter' as const } : undefined;
        // CR-4887 round 2 follow-up: the Customer Group dropdown is labelled
        // "Customer Group" and should show the logged-in customer's own
        // group (Skybridge Components), not the supplier-side groups the
        // customer serves. Drop supplier_filter from the customer-groups
        // call so the backend returns the customer's effective group via
        // the round-2 _effective_customer_company_id helper.
        const [companiesRes, sitesRes, groupsRes, customerSitesRes, warehousesRes] = await Promise.all([
          coreAPI.getAccessibleCompanies(filterContext).catch(() => ({ data: { results: [] } })),
          coreAPI.getAccessibleSites(filterContext).catch(() => ({ data: [] })),
          coreAPI.getCustomerGroupsForFilter(undefined).catch(() => ({ data: { results: [] } })),
          coreAPI.getCustomerSitesForFilter().catch(() => ({ data: { results: [] } })),
          coreAPI.getAccessibleWarehouses().catch(() => ({ data: [] })),
        ]);
        const accessibleCompanies = companiesRes.data.results || companiesRes.data || [];
        if (portalIsCustomer) {
          setSuppliers(accessibleCompanies.filter((c: Company) => c.company_type === 'supplier'));
        } else {
          setSuppliers([]);
        }
        // Customer-Site dropdown source. Portal-aware on the backend:
        // supplier portal -> traded customers; customer portal -> own
        // group siblings; helix -> all customers.
        setCustomers(customerSitesRes.data.results || []);
        const siteData = sitesRes.data as unknown;
        const accessibleSites = Array.isArray(siteData) ? siteData : ((siteData as { results?: unknown[] })?.results || []);
        setSites(accessibleSites);
        setCustomerGroups(groupsRes.data.results || []);
        const whData = warehousesRes.data as unknown;
        const accessibleWarehouses = Array.isArray(whData) ? whData : ((whData as { results?: unknown[] })?.results || []);
        setWarehouses(accessibleWarehouses as Warehouse[]);
      }
      setCommodityGroups([]);
      setCommodityTypes([]);
    } catch {
      setCustomers([]);
      setSuppliers([]);
      setSites([]);
      setWarehouses([]);
      setCustomerGroups([]);
      setCommodityGroups([]);
      setCommodityTypes([]);
    }
  };

  const fetchSavedFilters = async (isInitialLoad: boolean = true) => {
    try {
      const response = await api.get('/core/dashboard-filter-presets/');
      setSavedFilters(response.data.results || response.data || []);
      const defaultFilter = (response.data.results || response.data || []).find((f: SavedDashboardFilter) => f.is_default);
      if (defaultFilter) loadFilterPreset(defaultFilter, isInitialLoad);
    } catch (err) {
      console.error('Failed to fetch saved filters:', err);
    }
  };

  const handleSaveFilter = async () => {
    if (!filterName.trim()) {
      showToast('Please enter a filter name', 'error');
      return;
    }
    try {
      const payload = {
        name: filterName, description: filterDescription,
        date_from: filters.dateFrom || null, date_to: filters.dateTo || null,
        customer: filters.customer || null, supplier: filters.supplier || null,
        site: filters.site || null, currency: filters.currency,
        is_shared: isShared, is_default: isDefault,
      };
      await api.post('/core/dashboard-filter-presets/', payload);
      setShowSaveFilterDialog(false);
      setFilterName('');
      setFilterDescription('');
      setIsShared(false);
      setIsDefault(false);
      fetchSavedFilters(false);
      showToast('Filter saved successfully!', 'success');
    } catch (err: unknown) {
      console.error('Failed to save filter:', err);
      showToast(getApiErrorMessage(err, 'Failed to save filter'), 'error');
    }
  };

  const loadFilterPreset = (preset: SavedDashboardFilter, skipFetch: boolean = false) => {
    const newFilters: DashboardFilterState = {
      dateFrom: preset.date_from || '', dateTo: preset.date_to || '',
      customer: preset.customer?.toString() || '', supplier: preset.supplier?.toString() || '',
      site: preset.site?.toString() || '', currency: preset.currency || preferences.currency,
      customerGroup: preset.customer_group?.toString() || '',
      strategicOnly: preset.strategic_only || false,
      customerType: preset.customer_type || '',
      commodityGroup: preset.commodity_group?.toString() || '',
      commodityType: preset.commodity_type?.toString() || '',
      siteCountry: '', customerCountry: '', customerCode: '', orderType: '', warehouse: '', dataMode: 'historical', division: '',
      approvalStage: '', riskCategory: '',
    };
    setFilters(newFilters);
    setActiveFilterPresetId(preset.id);
    if (!skipFetch) fetchDashboardData(true, newFilters);
  };

  const fetchDashboardData = async (forceRefresh: boolean = false, filterOverrides?: DashboardFilterState, bypassServerCache: boolean = false) => {
    // Track fetch ID to discard stale responses from superseded fetches
    const currentFetchId = ++fetchIdRef.current;
    const effectiveFilters = filterOverrides || filters;

    const cacheFilters = {
      ...effectiveFilters,
      display_currency: preferences.currency || 'GBP',
      viewLevel, selectedCompanyId, selectedSiteId, selectedGroupId,
      portalIsCustomer,
      dataPeriod, orderType,
      includeDateAdherence: includeDateAdherenceRef.current,
      includeCompanyKpi: includeCompanyKpiRef.current,
      includeGroupKpi: includeGroupKpiRef.current,
      includeCostData: includeCostDataRef.current,
    };
    const filterKey = JSON.stringify(cacheFilters);
    // CR-4671: Bump cache version so every client's stale sessionStorage
    // entry is orphaned on next dashboard load. The previous OTD chart
    // bucketing bug left cached DashboardData where otdData.early was
    // hardcoded to 0; without bumping the version, clients would keep
    // serving that stale object for up to 5 minutes after the deploy and
    // still disagree with the live drill-down numbers.
    const CACHE_VERSION = 'v8';
    const CACHE_KEY = `dashboard_data_${CACHE_VERSION}_${filterKey}`;

    if (!forceRefresh) {
      const cachedData = Cache.get(CACHE_KEY) as DashboardData | null;
      if (cachedData) {
        setData(cachedData);
        hasInitiallyLoaded.current = true;
        setLoading(false);
        return;
      }
    }

    try {
      fetchInProgress.current = true;
      setLoading(true);
      setError(null);

      const filterParams: Record<string, string | number | boolean> = {};
      if (effectiveFilters.dateFrom) filterParams['date_from'] = effectiveFilters.dateFrom;

      // CR-4339: Historical mode caps date_to to end of previous month
      // (excludes current incomplete month). Live mode uses actual date_to.
      if (effectiveFilters.dataMode === 'historical') {
        const now = new Date();
        const endOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0)
          .toISOString().split('T')[0];
        const userDateTo = effectiveFilters.dateTo || endOfPrevMonth;
        filterParams['date_to'] = userDateTo < endOfPrevMonth ? userDateTo : endOfPrevMonth;
      } else if (effectiveFilters.dateTo) {
        filterParams['date_to'] = effectiveFilters.dateTo;
      }

      if (effectiveFilters.customer) filterParams['customer'] = effectiveFilters.customer;
      if (effectiveFilters.supplier) filterParams['supplier'] = effectiveFilters.supplier;
      if (effectiveFilters.site) filterParams['site'] = effectiveFilters.site;
      if (effectiveFilters.customerType) filterParams['customer_type'] = effectiveFilters.customerType;
      // CR-4557: Pass country filters to backend
      if (effectiveFilters.siteCountry) filterParams['site_country'] = effectiveFilters.siteCountry;
      if (effectiveFilters.customerCountry) filterParams['customer_country'] = effectiveFilters.customerCountry;
      // CR-4349: Division filter — backend supports `group` query param
      if (effectiveFilters.division) filterParams['group'] = effectiveFilters.division;
      // CR-4886: Customer Group filter — wire to backend so combo
      // (customer_group + customer site) actually narrows the dataset
      // instead of silently dropping the group selection.
      if (effectiveFilters.customerGroup) filterParams['customer_group'] = effectiveFilters.customerGroup;
      filterParams['display_currency'] = preferences.currency || 'GBP';
      if (dataPeriod) filterParams['period'] = dataPeriod;
      if (orderType && orderType !== 'all') filterParams['order_type'] = orderType;
      // Perf: let the server cache (5 min) serve auto-loads — dashboard
      // stats compute is ~8s uncached. Backend signals invalidate the
      // cache on PO/line/GRN/NCR writes so stale data is no longer a
      // concern. Only the manual Refresh button bypasses the server
      // cache via data_mode=live.
      if (bypassServerCache) {
        filterParams['data_mode'] = 'live';
      }

      // Perf: opt into the lazy gating pattern. Default tab gets only summary
      // KPIs + charts; per-tab sections are fetched on demand below.
      filterParams['lazy'] = '1';
      if (includeDateAdherenceRef.current) {
        filterParams['include_date_adherence'] = '1';
      }
      if (includeCompanyKpiRef.current) {
        filterParams['include_company_kpi'] = '1';
      }
      if (includeGroupKpiRef.current) {
        filterParams['include_group_kpi'] = '1';
      }
      if (includeCostDataRef.current) {
        filterParams['include_cost_data'] = '1';
      }

      const viewParams = getViewQueryParams();
      if (viewParams['group']) filterParams['group'] = viewParams['group'];
      if (viewParams['company'] && !portalIsCustomer) {
        filterParams['supplier'] = viewParams['company'];
      }
      if (viewParams['site']) filterParams['site'] = viewParams['site'];

      const viewContextParams: Record<string, string | number> = {};
      if (viewParams['group']) viewContextParams['group'] = viewParams['group'];
      if (viewParams['company']) viewContextParams['company'] = viewParams['company'];
      if (viewParams['site']) viewContextParams['site'] = viewParams['site'];

      const [coreStats, purchasingStats, qualityStats, customerStats, perfSettings] = await Promise.all([
        coreAPI.getDashboardStats(viewContextParams),
        api.get('/purchasing/orders/dashboard_stats/', { params: filterParams }),
        qualityAPI.getNCRStats(viewContextParams),
        customersAPI.getDashboardStats(viewContextParams),
        performanceSettingsAPI.getSettings().catch(() => null),
      ]);

      const mappedData: DashboardData = {
        companies: { total: coreStats.data.total_companies || 0, suppliers: coreStats.data.total_suppliers || 0, customers: coreStats.data.total_customers || 0 },
        sites: { total: coreStats.data.total_sites || 0 },
        users: { total: coreStats.data.total_users || 0, pendingApprovals: coreStats.data.pending_approvals || 0 },
        purchasing: { totalPOs: purchasingStats.data.total_pos || 0, openPOs: purchasingStats.data.open_pos || 0, totalLines: purchasingStats.data.total_lines || 0, byStatus: purchasingStats.data.by_status || {}, totalValue: parseFloat(purchasingStats.data.total_value?.toString() || '0') },
        // CR-4641 round 2: preserve null for ppm/otif/otd so chart visibility
        // can be driven by data presence (backend returns null when the
        // CustomerProfile flag excludes all matching customers). Old `|| 0`
        // collapsed null → 0, hiding the signal.
        quality: { ppm: purchasingStats.data.ppm ?? null, ppmTarget: purchasingStats.data.targets?.ppm ?? 1500, ncrs: purchasingStats.data.ncrs || 0, ncrsByStatus: qualityStats.data.by_status || {}, ncrsBySeverity: qualityStats.data.by_severity || {}, otif: purchasingStats.data.otif ?? null, otifTarget: purchasingStats.data.targets?.otif ?? 85 },
        delivery: { otd: purchasingStats.data.otd ?? null, otdTarget: purchasingStats.data.targets?.otd ?? 90, appeals: purchasingStats.data.appeals || 0, appealsBreakdown: purchasingStats.data.appeals_breakdown || undefined, openOrderValue: parseFloat(purchasingStats.data.open_order_value?.toString() || '0') },
        customers: { total: customerStats.data.total_customers || 0, active: customerStats.data.active_customers || 0, preferred: customerStats.data.preferred_customers || 0, avgQuality: customerStats.data.avg_quality_rating || 0, avgDelivery: customerStats.data.avg_delivery_rating || 0 },
        cost: { totalSpend: parseFloat(purchasingStats.data.total_spend?.toString() || '0'), historicalArrears: parseFloat(purchasingStats.data.arrears_value?.toString() || '0') },
        charts: {
          ppmTrend: purchasingStats.data.charts?.ppmTrend || [], otifData: purchasingStats.data.charts?.otifData || [],
          spendByMonth: purchasingStats.data.charts?.spendByMonth || [], otdData: purchasingStats.data.charts?.otdData || [],
          liveNCRsOverdue: purchasingStats.data.charts?.liveNCRsOverdue || [], historicalArrears: purchasingStats.data.charts?.historicalArrears || [], arrearsBacklog: purchasingStats.data.charts?.arrearsBacklog || [],
          appealsTrend: purchasingStats.data.charts?.appealsTrend || [], ppmNCRs: purchasingStats.data.charts?.ppmNCRs || [],
          ppmGRNs: purchasingStats.data.charts?.ppmGRNs || [], otifLines: purchasingStats.data.charts?.otifLines || [],
          otifPOL: purchasingStats.data.charts?.otifPOL || [], otifArrears: purchasingStats.data.charts?.otifArrears || [],
          otifFailed: purchasingStats.data.charts?.otifFailed || [], spendLines: purchasingStats.data.charts?.spendLines || [],
          otdOntime: purchasingStats.data.charts?.otdOntime || [], otdEarly: purchasingStats.data.charts?.otdEarly || [],
          otdLate: purchasingStats.data.charts?.otdLate || [], otdAll: purchasingStats.data.charts?.otdAll || [],
          ncrDetails: purchasingStats.data.charts?.ncrDetails || [], arrearsDetails: purchasingStats.data.charts?.arrearsDetails || [],
          milestones: purchasingStats.data.charts?.milestones || [], dateAdherenceData: purchasingStats.data.charts?.dateAdherenceData || [],
        },
        siteKPI: { otifBySite: purchasingStats.data.company_kpi?.otif_by_company || [], deliveryBySite: purchasingStats.data.company_kpi?.delivery_by_company || [], ppmBySite: purchasingStats.data.company_kpi?.ppm_by_company || [], ncrBreakdown: purchasingStats.data.company_kpi?.ncr_breakdown || [] },
        groupKpi: purchasingStats.data.group_kpi || undefined,
        costData: purchasingStats.data.costData || undefined,
        targets: purchasingStats.data.targets || undefined,
        delivery_metric_mode: perfSettings?.data?.delivery_metric_mode || 'both',
        // CR-5257 + CR-5258 — operational status snapshot from dashboard_stats
        operational: purchasingStats.data.operational || undefined,
      };

      // Discard stale responses if a newer fetch has started
      if (currentFetchId !== fetchIdRef.current) return;

      setData(mappedData);
      hasInitiallyLoaded.current = true;
      Cache.set(CACHE_KEY, mappedData, 5 * 60 * 1000);
    } catch (err: unknown) {
      if (currentFetchId !== fetchIdRef.current) return;
      if (axios.isCancel(err) || (err instanceof Error && err.name === 'CanceledError')) return;
      if (err && typeof err === 'object' && 'code' in err && (err.code === 'ECONNABORTED' || err.code === 'ERR_CANCELED')) return;
      console.error('Failed to fetch dashboard data:', err);
      setError(getApiErrorMessage(err, 'Failed to load dashboard data'));
    } finally {
      // Only update loading state if this is still the latest fetch.
      // Otherwise a superseded fetch would clear loading while the new one is still running.
      if (currentFetchId === fetchIdRef.current) {
        fetchInProgress.current = false;
        setLoading(false);
      }
    }
  };

  const handleApplyFilters = () => fetchDashboardData(true);

  const handleResetFilters = () => {
    setActiveFilterPresetId(null);
    const resetDateRange = getLast12MonthsRange();
    setFilters({
      dateFrom: resetDateRange.dateFrom, dateTo: resetDateRange.dateTo,
      customer: '', supplier: '', site: '', currency: preferences.currency,
      customerGroup: '', strategicOnly: false, customerType: '',
      commodityGroup: '', commodityType: '', siteCountry: '', customerCountry: '',
      customerCode: '', orderType: '', warehouse: '', dataMode: 'historical', division: '',
      approvalStage: '', riskCategory: '',
    });
    setTimeout(() => fetchDashboardData(true), 100);
  };

  // Lazy-load the per-customer/per-month adherence dataset. Called when
  // the user opens the Date Adherence tab — avoids paying for the 100KB+
  // payload on every default dashboard load.
  const loadDateAdherence = async () => {
    if (includeDateAdherenceRef.current) return;
    includeDateAdherenceRef.current = true;
    await fetchDashboardData(true);
  };

  const loadCompanyKpi = async () => {
    if (includeCompanyKpiRef.current) return;
    includeCompanyKpiRef.current = true;
    await fetchDashboardData(true);
  };

  const loadGroupKpi = async () => {
    if (includeGroupKpiRef.current) return;
    includeGroupKpiRef.current = true;
    await fetchDashboardData(true);
  };

  const loadCostData = async () => {
    if (includeCostDataRef.current) return;
    includeCostDataRef.current = true;
    await fetchDashboardData(true);
  };

  const handleExportReport = async (activeTab?: string) => {
    if (!data) {
      showToast('No data to export', 'error');
      return;
    }
    try {
      setExporting(true);
      const rows: string[] = [];

      const title = activeTab === 'compliance' ? 'Compliance Dashboard Report' : 'Dashboard Report';
      rows.push(title);
      rows.push(`Generated: ${new Date().toLocaleString()}`);
      rows.push(`Currency: ${preferences.currency}`);
      if (viewLevel) {
        rows.push(`View Level: ${viewLevel}`);
        if (selectedCompanyName) rows.push(`Company: ${selectedCompanyName}`);
        if (selectedSiteName) rows.push(`Company: ${selectedSiteName}`);
        if (selectedGroupName) rows.push(`Group: ${selectedGroupName}`);
      }
      rows.push('');

      // CR-4760: Compliance dashboard exports its own focused dataset.
      // The purchasing metrics below are irrelevant to that view.
      if (activeTab === 'compliance') {
        const { fetchComplianceExportData } = await import('../components/dashboard/complianceExportData');
        // CR-4760 rework: pass the same view context ComplianceTab uses when
        // it fetches /drilldown/customers_by_approval/buckets/, otherwise the
        // exported Approval Stage counts drift from what's on screen.
        const compliance = await fetchComplianceExportData({
          isComplianceMode: _isComplianceMode,
          viewLevel: viewLevel || undefined,
          viewQueryParams: getViewQueryParams(),
        });

        rows.push('Customers by Approval Stage', 'Stage,Count,Percentage');
        compliance.customersByApproval.forEach((b) => {
          rows.push(`${b.name},${b.value},${b.percentage}%`);
        });
        rows.push('');

        rows.push('Customers by Risk Category', 'Category,Count,Percentage');
        compliance.customersByRisk.forEach((b) => {
          rows.push(`${b.name},${b.value},${b.percentage}%`);
        });
        rows.push('');

        rows.push('Questionnaires by Status', 'Status,Count,Percentage');
        compliance.questionnairesByStatus.forEach((b) => {
          rows.push(`${b.name},${b.value},${b.percentage}%`);
        });
        rows.push('');

        if (!compliance.isComplianceMode) {
          rows.push('Contracts by Renewal', 'Type,Count,Percentage');
          compliance.contractsByAutoRenew.forEach((b) => {
            rows.push(`${b.name},${b.value},${b.percentage}%`);
          });
          rows.push('');

          rows.push('Summary');
          rows.push(`Live Contracts,${compliance.liveContracts}`);
          rows.push(`Customers Awaiting Review,${compliance.docsAwaitingRead}`);
          rows.push('');
        } else {
          rows.push('Summary');
          rows.push(`Customers Awaiting Review,${compliance.docsAwaitingRead}`);
          rows.push('');
        }

        const csvContent = rows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `compliance-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast('Report exported successfully!', 'success');
        return;
      }

      rows.push('Quality Metrics');
      rows.push(`PPM,${data.quality.ppm}`, `PPM Target,${data.quality.ppmTarget}`, `NCRs,${data.quality.ncrs}`, `OTIF,${data.quality.otif}%`, `OTIF Target,${data.quality.otifTarget}%`, '');

      rows.push('Delivery Metrics');
      rows.push(`OTD,${data.delivery.otd}%`, `OTD Target,${data.delivery.otdTarget}%`, `Appeals,${data.delivery.appeals}`, `Open Order Value,${data.delivery.openOrderValue}`, '');

      rows.push('Cost Metrics');
      rows.push(`Total Spend,${data.cost.totalSpend}`, `Historical Arrears,${data.cost.historicalArrears}`, '');

      rows.push('Purchasing');
      rows.push(`Total POs,${data.purchasing.totalPOs}`, `Total Lines,${data.purchasing.totalLines}`, `Total Value,${data.purchasing.totalValue}`, '');

      rows.push('Customers');
      rows.push(`Total,${data.customers.total}`, `Active,${data.customers.active}`, `Strategic,${data.customers.preferred}`, '');

      if (data.charts.ppmTrend && data.charts.ppmTrend.length > 0) {
        rows.push('PPM Trend', 'Month,PPM,Target');
        data.charts.ppmTrend.forEach((item: Record<string, unknown>) => {
          rows.push(`${item['month'] || ''},${item['ppm'] || 0},${item['target'] || 0}`);
        });
        rows.push('');
      }
      if (data.charts.otifData && data.charts.otifData.length > 0) {
        rows.push('OTIF Trend', 'Month,OTIF %,Target,OTIF Lines,Arrears Lines,Failed Lines');
        data.charts.otifData.forEach((item: Record<string, unknown>) => {
          rows.push(`${item['month'] || ''},${item['otif'] || 0},${item['target'] || 0},${item['otifCount'] || 0},${item['arrears'] || 0},${item['failed'] || 0}`);
        });
        rows.push('');
      }
      if (data.charts.otdData && data.charts.otdData.length > 0) {
        rows.push('OTD Trend', 'Month,OTD %,Target,On Time,Early,Late');
        data.charts.otdData.forEach((item: Record<string, unknown>) => {
          rows.push(`${item['month'] || ''},${item['otd'] || 0},${item['target'] || 0},${item['onTime'] || 0},${item['early'] || 0},${item['late'] || 0}`);
        });
        rows.push('');
      }
      if (data.charts.spendByMonth && data.charts.spendByMonth.length > 0) {
        rows.push('Spend by Month', 'Month,Spend');
        data.charts.spendByMonth.forEach((item: Record<string, unknown>) => {
          rows.push(`${item['month'] || ''},${item['spend'] || 0}`);
        });
        rows.push('');
      }

      const csvContent = rows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('Report exported successfully!', 'success');
    } catch (err) {
      console.error('Export failed:', err);
      showToast('Failed to export report', 'error');
    } finally {
      setExporting(false);
    }
  };

  return {
    data, loading, error, filters, setFilters,
    customers, suppliers, sites, warehouses, customerGroups, commodityGroups, commodityTypes,
    savedFilters, activeFilterPresetId,
    showSaveFilterDialog, setShowSaveFilterDialog,
    filterName, setFilterName, filterDescription, setFilterDescription,
    isShared, setIsShared, isDefault, setIsDefault,
    toast, setToast, exporting,
    dataPeriod, setDataPeriod, orderType, setOrderType,
    showToast, handleApplyFilters, handleResetFilters,
    handleSaveFilter, loadFilterPreset, fetchDashboardData,
    handleExportReport,
    loadDateAdherence,
    loadCompanyKpi,
    loadGroupKpi,
    loadCostData,
  };
}
